/**
 * The props every manager PIN input carries (DNKADM-0458, DNKADM-0471). A `type="password"`
 * field is treated by Chrome as a saved-login credential (it ignores `autocomplete="off"`
 * there), so a shared front-desk computer could save a manager PIN and autofill it for STAFF.
 * The PIN is therefore a numeric TEXT input: a one-time-code autocomplete hint, a per-instance
 * name with no pass/pwd/pin substring, and the password-manager ignore flags.
 *
 * Masking (DNKADM-0471): Firefox has no `-webkit-text-security`, so CSS masking showed the
 * digits there. The field instead DISPLAYS one bullet per digit while the real PIN lives in
 * the caller's state; each edit is mapped back onto the real digits ({@link nextManagerPin}).
 * That masks in every browser and keeps the digits out of the DOM. Non-digits are dropped and
 * the PIN is capped at {@link MANAGER_PIN_MAX_DIGITS} (the connection PIN is 4-8 digits).
 *
 * Caret (DNKADM-0476 F-63): the field's text is replaced by the re-masked value on every edit,
 * which moves the browser caret to the end. The hook remembers where the edit left the caret
 * ({@link editManagerPin}) and puts it back after the render, so typing or deleting mid-PIN keeps
 * going from the same place, including after an ignored non-digit.
 *
 * Spread the result onto the TextField: `<TextField {...useManagerPinInputProps(pin, setPin)} />`.
 */
import { useLayoutEffect, useRef, useState, type ChangeEvent, type RefObject } from 'react';

/** Digits only: an id like React's base-32 `useId` can spell "pin" or "pass" by chance. */
let nextFieldNumber = 0;

export const MANAGER_PIN_MAX_DIGITS = 8;

/** The character shown for each PIN digit. */
export const MANAGER_PIN_MASK = '•';

export interface ManagerPinHtmlInputProps {
  inputMode: 'numeric';
  autoComplete: 'one-time-code';
  name: string;
  'data-1p-ignore': true;
  'data-lpignore': 'true';
  'data-form-type': 'other';
}

export interface ManagerPinInputProps {
  /** The masked text the field shows: one bullet per digit, never the digits. */
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  /** The input element, so the caret can be restored after the masked value re-renders. */
  inputRef: RefObject<HTMLInputElement | null>;
  slotProps: { htmlInput: ManagerPinHtmlInputProps };
}

/** The masked text for a PIN. */
export function maskManagerPin(pin: string): string {
  return MANAGER_PIN_MASK.repeat(pin.length);
}

/**
 * The real PIN after an edit to the masked field. `displayed` is the field's new text (bullets
 * for the digits it kept, plain characters for what was just typed or pasted) and `caret` is
 * where the caret landed. The typed run ends at the caret, so the kept bullets before it are
 * the old PIN's leading digits and the kept bullets after it are its trailing digits: that
 * covers typing, Backspace, Delete, and pasting over a selection. Non-digits are dropped.
 */
export function nextManagerPin(previous: string, displayed: string, caret: number | null): string {
  return editManagerPin(previous, displayed, caret).pin;
}

/**
 * {@link nextManagerPin} plus where the caret belongs in the re-masked field: just after the
 * digits the edit kept or typed before it (non-digits dropped), never past the PIN's end.
 */
export function editManagerPin(
  previous: string,
  displayed: string,
  caret: number | null,
): { pin: string; caret: number } {
  const end = caret === null ? displayed.length : Math.min(Math.max(caret, 0), displayed.length);
  let start = end;
  while (start > 0 && displayed[start - 1] !== MANAGER_PIN_MASK) start -= 1;
  const kept = displayed.slice(0, start) + displayed.slice(end);
  if ([...kept].some((ch) => ch !== MANAGER_PIN_MASK)) {
    // Typed characters outside the run before the caret: the edit can't be placed, so the
    // typed digits are taken as the whole entry.
    const pin = digitsOnly(displayed);
    return { pin, caret: pin.length };
  }
  const keptAfter = displayed.length - end;
  const head = previous.slice(0, Math.min(start, previous.length));
  const typed = digitsOnly(displayed.slice(start, end));
  const tail =
    keptAfter > 0 ? previous.slice(Math.max(previous.length - keptAfter, head.length)) : '';
  const pin = digitsOnly(head + typed + tail);
  return { pin, caret: Math.min(head.length + typed.length, pin.length) };
}

function digitsOnly(text: string): string {
  return text.replace(/\D/g, '').slice(0, MANAGER_PIN_MAX_DIGITS);
}

export function useManagerPinInputProps(
  pin: string,
  onPinChange: (pin: string) => void,
): ManagerPinInputProps {
  const [name] = useState(() => `mgr-code-${(nextFieldNumber += 1)}`);
  const inputRef = useRef<HTMLInputElement | null>(null);
  /** Where the last edit left the caret, applied once after the re-masked value renders. */
  const caretRef = useRef<number | null>(null);
  // An edit that leaves the PIN unchanged (a letter typed mid-PIN) re-renders nothing upstream,
  // and the field's text is put back with the caret at the end; this tick re-renders the field
  // on every edit so the caret is restored either way.
  const [, setCaretTick] = useState(0);
  useLayoutEffect(() => {
    const caret = caretRef.current;
    const input = inputRef.current;
    if (caret === null || input === null) return;
    caretRef.current = null;
    if (input.ownerDocument.activeElement !== input) return;
    const at = Math.min(caret, input.value.length);
    input.setSelectionRange(at, at);
  });
  return {
    value: maskManagerPin(pin),
    onChange: (event) => {
      const input = event.target;
      const edit = editManagerPin(pin, input.value, input.selectionEnd);
      caretRef.current = edit.caret;
      setCaretTick((tick) => tick + 1);
      onPinChange(edit.pin);
    },
    inputRef,
    slotProps: {
      htmlInput: {
        inputMode: 'numeric',
        autoComplete: 'one-time-code',
        name,
        'data-1p-ignore': true,
        'data-lpignore': 'true',
        'data-form-type': 'other',
      },
    },
  };
}
